import { DeepMap, FieldError, FieldValues } from 'react-hook-form';

import { notifyError } from 'shared/Toast/Toast';

import { logDataDogEvent } from './datadog';

export const CONTROLLED_SUBSTANCE_PHOTO_ID_REQUIRED = 'CONTROLLED_SUBSTANCE_PHOTO_ID_REQUIRED';
export const MODALITY_NOT_AVAILABLE = 'MODALITY_NOT_AVAILABLE';

export const PROSPECT_ACCOUNT_NOT_REGISTERED = 'PROSPECT_ACCOUNT_NOT_REGISTERED';

/** The sentence the API pairs with its 403 for an account it classifies as signup-in-progress. */
export const UNFINISHED_SIGNUP_MESSAGE = "You haven't finished signup.";

/**
 * Extract the nested API `errorCode` from an RTK Query / fetch rejection payload.
 *
 * Backend shape for typed errors (e.g. ControlledSubstancePhotoIdRequiredError):
 * `{ data: { message, data: { errorCode } } }`
 */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return undefined;
  }
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object' || !('data' in data)) {
    return undefined;
  }
  const nested = (data as { data?: unknown }).data;
  if (!nested || typeof nested !== 'object' || !('errorCode' in nested)) {
    return undefined;
  }
  const errorCode = (nested as { errorCode?: unknown }).errorCode;
  return typeof errorCode === 'string' ? errorCode : undefined;
};

export const isControlledSubstancePhotoIdRequiredError = (error: unknown): boolean =>
  getApiErrorCode(error) === CONTROLLED_SUBSTANCE_PHOTO_ID_REQUIRED;

/**
 * The signer proved they own this mailbox, but it belongs to a staff-created sales lead that cannot be
 * converted (LMD-2715) — so there is no account to sign in to and the person should be sent to signup.
 * Matched on the error code rather than the sentence, which the backend is free to reword.
 */
export const isProspectAccountNotRegisteredError = (error: unknown): boolean =>
  getApiErrorCode(error) === PROSPECT_ACCOUNT_NOT_REGISTERED;

/** The 400 `POST /appointments` returns when the chosen visit option is unavailable at booking. */
export const isModalityNotAvailableError = (error: unknown): boolean =>
  getApiErrorCode(error) === MODALITY_NOT_AVAILABLE;

/**
 * The 403 the API answers for an account whose signup it considers unfinished.
 *
 * Its route allowlist admits a narrow set of paths for such an account, and refuses the rest with
 * this exact pair. It is an expected account state rather than a failure, which is why callers
 * suppress it — but only it. Matched on the sentence as well as the status because a bare 403 is
 * also how the API refuses things that really are wrong.
 */
export const isUnfinishedSignupError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { data, status } = error as { data?: unknown; status?: unknown };
  if (status !== 403 || !data || typeof data !== 'object') return false;
  return (data as { message?: unknown }).message === UNFINISHED_SIGNUP_MESSAGE;
};

/**
 * A readable cause for an RTK Query rejection.
 *
 * A rejection is a plain `{ status, data }` object, not an `Error`, so `(e as Error).message` is
 * `undefined` on every one of them — and `undefined ?? 'Error fetching data'` means the fallback
 * always wins. Every failure then reads the same, and a 403 is indistinguishable from a 502 or a
 * dropped connection to whoever reads the next bug report. The status is appended for that reason:
 * the backend's sentence says what happened, the code says which class of failure it was.
 */
export const getRequestErrorMessage = (
  e: unknown,
  fallback = 'Something went wrong, please try again later'
): string => {
  const error = (e ?? {}) as {
    data?: unknown;
    message?: unknown;
    status?: unknown;
  };

  if (error.status === 'FETCH_ERROR') return 'Network request failed';
  if (error.status === 'TIMEOUT_ERROR') return 'The request timed out';

  /*
   * A response the client could not parse — typically an HTML page from a proxy or gateway rather
   * than the API's JSON. RTK Query puts the raw body in `data` and the real HTTP code in
   * `originalStatus`, so the generic path below would hand an entire error page to whatever
   * displays this and drop the one useful part. Report the status and say nothing else.
   */
  if (error.status === 'PARSING_ERROR') {
    const { originalStatus } = error as { originalStatus?: unknown };
    return typeof originalStatus === 'number' ? `${fallback} (${originalStatus})` : fallback;
  }

  const fromPayload = () => {
    if (typeof error.data === 'string') return error.data;
    if (typeof error.message === 'string') return error.message;
    const data = error.data as { message?: unknown } | undefined;
    if (data && typeof data.message === 'string') return data.message;
    return undefined;
  };

  const message = fromPayload() ?? fallback;
  return typeof error.status === 'number' ? `${message} (${error.status})` : message;
};

export const handleRequestCatch = (
  e: MessageEvent,
  alternativeMessage = 'Something went wrong, please try again later'
) => {
  if (isUnfinishedSignupError(e)) {
    return;
  }

  const getErrorMessage = () => {
    if ('status' in e && e.status === 'FETCH_ERROR') {
      return 'Fetch error';
    }
    if (typeof e.data === 'string') {
      return e.data;
    }
    if ('message' in e && typeof e.message === 'string') {
      return e.message;
    }
    if ('data' in e && e.data && 'message' in e.data && typeof e.data.message === 'string') {
      return e.data.message;
    }
    return 'Uncaught Error';
  };
  logDataDogEvent({
    context: {
      componentStack: 'handleRequestCatch',
      ...e
    },
    error: new Error(getErrorMessage()),
    level: 'warn',
    message: getErrorMessage()
  });
  if ('status' in e && e.status === 500) {
    return;
  }
  notifyError(e.data?.message || alternativeMessage);
};

export const onFormError = (errors: DeepMap<FieldValues, FieldError>) => {
  requestAnimationFrame(() => {
    const fieldNames = Object.keys(errors);

    const firstErrorName = fieldNames.find((name) => errors[name]);
    if (firstErrorName) {
      const element = document.querySelector(`[name="${firstErrorName}"]`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });
};
